import React, { useState, useEffect } from 'react';
import { User, Mail, Lock, Shield, Globe, CreditCard, RefreshCw, Trash2, Loader2, Check, X } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import NotificationBox from '../components/common/NotificationBox';

interface UserPanelProps {
  onLogout: () => void;
}

interface PaymentMethod {
  id: string;
  last4: string;
  brand: string;
  exp_month: number;
  exp_year: number;
}

interface Subscription {
  id: string;
  status: string;
  current_period_end: number;
  cancel_at_period_end: boolean;
}

const UserPanel: React.FC<UserPanelProps> = ({ onLogout }) => {
  const navigate = useNavigate();
  const { userInfo, isLoading, error, logout, fetchUserInfo } = useAuth();
  const [activeTab, setActiveTab] = useState('account');
  const [searchParams] = useSearchParams();
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successProduct, setSuccessProduct] = useState('');
  const [successPlan, setSuccessPlan] = useState('');
  
  // Password change states
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  const handleLogout = () => {
    logout();
    onLogout();
  };

  const TabButton: React.FC<{ id: string; label: string }> = ({ id, label }) => (
    <button
      onClick={() => setActiveTab(id)}
      className={`px-4 py-2 text-sm font-medium transition-all duration-200 ${
        activeTab === id 
          ? 'text-blue-400 border-b-2 border-blue-400' 
          : 'text-gray-400 hover:text-gray-300'
      }`}
    >
      {label}
    </button>
  );

  useEffect(() => {
    const success = searchParams.get('success');
    const product = searchParams.get('product');
    const plan = searchParams.get('plan');

    if (success && product && plan) {
      setSuccessProduct(product);
      setSuccessPlan(plan);
      setShowSuccessMessage(true);

      // Remove the success parameters from the URL without refreshing
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);

      // Hide the success message after 3 seconds
      const timer = setTimeout(() => {
        setShowSuccessMessage(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [searchParams]);

  const handlePasswordChange = async () => {
    if (newPassword !== repeatPassword) {
      setPasswordError('Passwords do not match!');
      return;
    }
    setPasswordError('');
    try {
      const response = await fetch('/update-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          currentPassword,
          newPassword 
        }),
      });
      const data = await response.json();
      if (data.code === 200) {
        setPasswordSuccess(true);
        setTimeout(() => {
          setShowPasswordModal(false);
          setCurrentPassword('');
          setNewPassword('');
          setRepeatPassword('');
          setPasswordSuccess(false);
        }, 1500);
      }
    } catch (error) {
      console.error('Error updating password:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-950 bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error || !userInfo) {
    return (
      <div className="min-h-screen bg-gray-950 bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 text-white flex items-center justify-center">
        <div className="text-red-400 text-center">
          <p className="text-lg mb-2">Error loading user information</p>
          <p className="text-sm">{error || 'No user information available'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 text-white flex flex-col">
      {showSuccessMessage && (
        <NotificationBox
          message={`Payment Succeeded, ${successProduct} Subscription Added for ${successPlan}`}
          type="success"
          onClose={() => setShowSuccessMessage(false)}
        />
      )}

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-gray-900/90 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50 shadow-[0_0_15px_rgba(0,0,0,0.3)] w-full max-w-md">
            <h2 className="text-xl font-semibold text-blue-400 mb-6">Password Reset</h2>
            <div className="space-y-4">
              <div>
                <label className="text-gray-400 text-sm">Current Password</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full mt-1 bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500"
                />
              </div>
              <div>
                <label className="text-gray-400 text-sm">New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full mt-1 bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500"
                />
              </div>
              <div>
                <label className="text-gray-400 text-sm">Repeat New Password</label>
                <input
                  type="password"
                  value={repeatPassword}
                  onChange={(e) => setRepeatPassword(e.target.value)}
                  className="w-full mt-1 bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500"
                />
                {passwordError && (
                  <p className="text-red-400 text-sm mt-1">{passwordError}</p>
                )}
                {passwordSuccess && (
                  <p className="text-green-400 text-sm mt-1">Password successfully reset!</p>
                )}
              </div>
              <div className="flex justify-end gap-4 mt-6">
                <button
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPasswordError('');
                    setPasswordSuccess(false);
                    setCurrentPassword('');
                    setNewPassword('');
                    setRepeatPassword('');
                  }}
                  className="text-gray-400 hover:text-gray-300 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePasswordChange}
                  className="px-4 py-2 bg-blue-500/10 text-blue-400 rounded-lg hover:bg-blue-500/20 transition-all duration-200"
                >
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex-grow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-4">
              <div>
                <h1 className="text-2xl font-bold text-white">Account</h1>
                <p className="text-gray-400">Information and settings regarding your account</p>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-800 mb-8">
            <div className="flex justify-between items-center">
              <div className="flex space-x-8">
                <TabButton id="account" label="My Account" />
                <TabButton id="products" label="Products" />
              </div>
              <button 
                onClick={handleLogout}
                className="text-sm text-gray-400 hover:text-gray-300 transition-colors duration-200 flex items-center gap-2 group"
              >
                <span className="group-hover:translate-x-[-2px] transition-transform duration-200">Logout</span>
                <svg 
                  width="14" 
                  height="14" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  className="opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                >
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                  <polyline points="16 17 21 12 16 7"></polyline>
                  <line x1="21" y1="12" x2="9" y2="12"></line>
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="space-y-8">
            {activeTab === 'account' && (
              <div className="grid gap-8">
                {/* Basic Info */}
                <div className="bg-gray-900/40 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50 shadow-[0_0_15px_rgba(0,0,0,0.3)] relative">
                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 rounded-xl" />
                  <div className="relative">
                    <h2 className="text-xl font-semibold text-blue-400 mb-6">Sign in & security</h2>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <User className="w-5 h-5 text-gray-400" />
                          <span className="text-gray-300">Username</span>
                        </div>
                        <span className="text-white">{userInfo?.username}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <Mail className="w-5 h-5 text-gray-400" />
                          <span className="text-gray-300">Email address</span>
                        </div>
                        <span className="text-white">{userInfo?.email}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <Lock className="w-5 h-5 text-gray-400" />
                          <span className="text-gray-300">Password</span>
                        </div>
                        <button
                          onClick={() => setShowPasswordModal(true)}
                          className="text-blue-400 text-sm hover:text-blue-300"
                        >
                          Reset
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Advanced Settings */}
                <div className="bg-gray-900/40 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50 shadow-[0_0_15px_rgba(0,0,0,0.3)] relative">
                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 rounded-xl" />
                  <div className="relative">
                    <h2 className="text-xl font-semibold text-blue-400 mb-6">Advanced Settings</h2>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <Shield className="w-5 h-5 text-gray-400" />
                          <span className="text-gray-300">HWID</span>
                        </div>
                        <div className="flex items-center space-x-4">
                          <span className="text-white">{userInfo?.hwid}</span>
                          <button className="text-blue-400 text-sm hover:text-blue-300">Reset</button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'products' && (
              <div className="grid gap-6">
                <div className="bg-gray-900/40 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50 shadow-[0_0_15px_rgba(0,0,0,0.3)] relative">
                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 rounded-xl" />
                  <div className="relative">
                    <h2 className="text-xl font-semibold text-blue-400 mb-6">Product information</h2>
                    <p className="text-gray-400 text-sm mb-6">Current status of products on your account.</p>
                    <div className="grid gap-4">
                      {userInfo?.products.map((product, index) => (
                        <div key={index} className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg">
                          <div>
                            <h3 className="text-white font-medium">{product.name}</h3>
                            <p className="text-gray-400 text-sm flex flex-row gap-2">
                              {product.expiryDate === null ? 'Permanent purchase' : 'Subscription'}

                              {product.expiryDate !== null && product.expiryDate === "Expired" && (
                                <h3 className="font-medium text-red-400">Expired</h3>
                              )}
                              {product.expiryDate !== null && product.expiryDate !== "Expired" && (
                                <h3 className="text-white font-medium">Expires {product.expiryDate}</h3>
                              )}

                              {product.periodEnd !== null && (
                                <div className="flex items-center gap-2">
                                  <h3 className="text-white font-medium">(Renews {product.periodEnd})</h3>
                                  <button 
                                    onClick={async () => {
                                      try {
                                        const response = await fetch('/tryCancelSubscription', {
                                          method: 'POST',
                                          headers: {
                                            'Content-Type': 'application/json',
                                          },
                                          body: JSON.stringify({ productId: product.id }),
                                        });
                                        const data = await response.json();
                                        if (data.code === 200) {
                                          await fetchUserInfo();
                                        }
                                      } catch (error) {
                                        console.error('Error canceling subscription:', error);
                                      }
                                    }}
                                    className="text-sm text-gray-400 hover:text-gray-300 transition-colors duration-200"
                                  >
                                    Cancel Renewal
                                  </button>
                                </div>
                              )}
                            </p>
                          </div>
                          <div className="flex items-center gap-4">
                            <button 
                              onClick={async () => {
                                try {
                                  const response = await fetch('/download', {
                                    method: 'POST',
                                    headers: {
                                      'Content-Type': 'application/json',
                                    },
                                    body: JSON.stringify({ productId: product.id }),
                                  });
                                  
                                  if (!response.ok) {
                                    throw new Error('Download failed');
                                  }
                                  
                                  // Get filename from Content-Disposition header
                                  const contentDisposition = response.headers.get('Content-Disposition');
                                  const filenameMatch = contentDisposition && contentDisposition.match(/filename="(.+)"/);
                                  const filename = filenameMatch ? filenameMatch[1] : 'download.zip';
                                  
                                  const blob = await response.blob();
                                  // Create a URL for the blob
                                  const url = window.URL.createObjectURL(blob);
                                  // Create a temporary link element
                                  const link = document.createElement('a');
                                  link.href = url;
                                  link.download = filename;
                                  document.body.appendChild(link);
                                  link.click();
                                  // Clean up
                                  document.body.removeChild(link);
                                  window.URL.revokeObjectURL(url);
                                } catch (error) {
                                  console.error('Error initiating download:', error);
                                }
                              }}
                              className="px-4 py-2 bg-blue-500/10 text-blue-400 rounded-lg hover:bg-blue-500/20 transition-all duration-200"
                            >
                              DOWNLOAD
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-gray-800 py-8 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div className="text-gray-400 text-sm">
              &copy; 2025 recoil.club. All rights reserved.
            </div>
            <div className="space-x-6">
              <button className="text-gray-400 text-sm hover:text-gray-300">Terms of Service</button>
              <button className="text-gray-400 text-sm hover:text-gray-300">Privacy Policy</button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default UserPanel; 